import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, ClipboardList } from 'lucide-react';
import axios from 'axios';
import { logError } from '../../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CLASS_META = {
  LOCK: { label: 'Roster Lock', short: 'LOCK', bg: '#14532d', text: '#fff' },
  LIKELY_IN: { label: 'Likely In', short: 'LIKELY IN', bg: '#166534', text: '#fff' },
  BUBBLE: { label: 'On the Bubble', short: 'BUBBLE', bg: '#FFB612', text: '#3b2f00' },
  LONG_SHOT: { label: 'Long Shot', short: 'LONG SHOT', bg: '#9a3412', text: '#fff' },
  PROJECTED_OUT: { label: 'Projected Out', short: 'PROJ. OUT', bg: '#57534e', text: '#fff' },
  RELEASED: { label: 'Released / Waived', short: 'RELEASED', bg: '#334155', text: '#fff' },
  INJURED: { label: 'Injured / Designation', short: 'INJ / DESIG.', bg: '#7c2d12', text: '#fff' },
  MADE_53: { label: 'Made the 53', short: 'MADE 53', bg: '#14532d', text: '#fff' },
  PRACTICE_SQUAD: { label: 'Practice Squad', short: 'PRACTICE SQUAD', bg: '#475569', text: '#fff' },
};

const TX_LABELS = {
  SIGNED: 'Signed', WAIVED: 'Waived', RELEASED: 'Released',
  PLACED_ON_IR: 'Placed on IR', ACTIVATED: 'Activated', RETIRED: 'Retired', CLAIMED: 'Claimed',
};

const CUT_TYPES = ['WAIVED', 'RELEASED', 'WAIVED_INJURED', 'RETIRED', 'TRADED'];
const ADD_TYPES = ['SIGNED', 'CLAIMED', 'ACTIVATED'];

const FILTERS = [
  { key: 'ALL', label: 'All' },
  { key: 'PROJECTED_53', label: 'Projected 53' },
  { key: 'LOCK', label: 'Locks' },
  { key: 'LIKELY_IN', label: 'Likely In' },
  { key: 'BUBBLE', label: 'Bubble' },
  { key: 'LONG_SHOT', label: 'Long Shots' },
  { key: 'PROJECTED_OUT', label: 'Projected Out' },
  { key: 'RELEASED', label: 'Cut / Waived' },
  { key: 'INJURED', label: 'Injured / Desig.' },
];

const daysUntil = (deadline) => {
  const diff = new Date(deadline) - new Date();
  return Math.max(Math.ceil(diff / 86400000), 0);
};

const ClassBadge = ({ classification }) => {
  const meta = CLASS_META[classification];
  if (!meta) return <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 border border-slate-300 px-1.5 py-0.5">Unclassified</span>;
  return (
    <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5" style={{ backgroundColor: meta.bg, color: meta.text }}>
      {meta.short}
    </span>
  );
};

const PlayerRow = ({ player }) => (
  <div data-testid={`roster-player-${player.id}`} className="flex items-start justify-between gap-3 py-2.5 px-3">
    <div className="min-w-0">
      <p className="text-sm font-bold text-slate-900">
        {player.name}
        <span className="ml-2 text-xs font-medium text-slate-400">
          {player.pos_detail || player.position}{player.jersey ? ` #${player.jersey}` : ''}
        </span>
        {player.stock === 'UP' && <TrendingUp className="inline w-3.5 h-3.5 ml-1.5 text-green-700" aria-label="Stock up" />}
        {player.stock === 'DOWN' && <TrendingDown className="inline w-3.5 h-3.5 ml-1.5 text-red-700" aria-label="Stock down" />}
      </p>
      {player.note && <p className="text-xs text-slate-600 leading-relaxed mt-0.5 italic">"{player.note}"</p>}
    </div>
    <div className="shrink-0 pt-0.5 flex flex-col items-end gap-1">
      <ClassBadge classification={player.classification} />
      {player.official_status && player.official_status !== 'ACTIVE_ROSTER' && (
        <span className="text-[9px] font-bold uppercase tracking-wide text-white bg-slate-900 px-1.5 py-0.5">
          Official: {player.official_status.replace(/_/g, ' ')}
        </span>
      )}
    </div>
  </div>
);

const SectionTitle = ({ children, color = '#BF0A30' }) => (
  <h3 className="font-heading text-base text-slate-900 uppercase tracking-wide flex items-center gap-2 border-b border-slate-200 pb-2 mb-3">
    <span className="inline-block w-1 h-5" style={{ backgroundColor: color }} />
    {children}
  </h3>
);

const RoadTo53 = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [posFilter, setPosFilter] = useState('ALL');
  const [view, setView] = useState('tracker');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (window.location.hash === '#road-to-53' && data) {
      setTimeout(() => document.getElementById('road-to-53')?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, [data]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(`${API}/roster/tracker`);
        setData(response.data);
      } catch (error) {
        logError('Failed to load roster tracker:', error);
      }
    };
    load();
  }, []);

  if (!data || data.players.length === 0) return null;

  const { settings, players, transactions, battles, counts, positions, changes = [] } = data;
  const cutMoves = transactions.filter((t) => CUT_TYPES.includes(t.type));
  const addMoves = transactions.filter((t) => ADD_TYPES.includes(t.type));
  const isFinal = settings.phase === 'final';
  const days = daysUntil(settings.deadline);
  const bubble = players.filter((p) => p.classification === 'BUBBLE');
  const stockWatch = players.filter((p) => p.stock);
  const projected = players.filter(
    (p) => (p.p53_explicit ? !!p.in_projected_53 : (['LOCK', 'LIKELY_IN', 'MADE_53'].includes(p.classification) || !!p.in_projected_53))
  ).filter((p) => !['RELEASED', 'PRACTICE_SQUAD'].includes(p.classification));

  const matchFilter = (p) => {
    if (filter === 'ALL') return true;
    if (filter === 'PROJECTED_53') return projected.includes(p);
    if (filter === 'RELEASED') return p.classification === 'RELEASED' || CUT_TYPES.includes(p.official_status);
    if (filter === 'INJURED') return p.classification === 'INJURED' || ['INJURED_RESERVE', 'PUP', 'NFI', 'WAIVED_INJURED'].includes(p.official_status);
    return p.classification === filter;
  };
  const filtered = players.filter(
    (p) => matchFilter(p) &&
      (posFilter === 'ALL' || p.position === posFilter) &&
      (!search || p.name.toLowerCase().includes(search.toLowerCase()))
  );
  const byPosition = positions
    .map((pos) => ({ pos, items: filtered.filter((p) => p.position === pos) }))
    .filter((g) => g.items.length > 0);
  const activeByPos = (pos) =>
    players.filter((p) => p.position === pos && !['RELEASED', 'PRACTICE_SQUAD'].includes(p.classification)).length;

  return (
    <section id="road-to-53" data-testid="road-to-53" className="bg-white border border-slate-200 scroll-mt-20">
      {/* Header */}
      <div className="bg-[#5A1414] px-5 py-6 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#FFB612]">
              DC Sports Report · Commanders
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-white uppercase tracking-tight mt-1">
              {isFinal ? 'Final 53-Man Roster' : 'Road to 53'}
            </h2>
            <p className="text-white/80 text-sm mt-1">Washington Commanders 53-Man Roster Tracker</p>
          </div>
          {!isFinal && (
            <div className="text-left sm:text-right">
              <p data-testid="cutdown-countdown" className="font-heading text-2xl text-[#FFB612] uppercase tracking-wide">
                {days} {days === 1 ? 'Day' : 'Days'} Until Cutdown
              </p>
              <p className="text-white/70 text-xs mt-1">Deadline: Sunday, Aug 30, 2026 · 6:00 PM ET</p>
            </div>
          )}
        </div>

        {/* Progress */}
        <div data-testid="roster-progress" className="mt-6 grid grid-cols-3 gap-3 text-center">
          <div className="bg-black/25 py-3 px-2">
            <p className="font-heading text-2xl text-white">{counts.active}</p>
            <p className="text-[10px] font-bold uppercase tracking-wide text-white/70">Current Players</p>
          </div>
          <div className="bg-black/25 py-3 px-2">
            <p className="font-heading text-2xl text-[#FFB612]">{counts.target}</p>
            <p className="text-[10px] font-bold uppercase tracking-wide text-white/70">Final Roster</p>
          </div>
          <div className="bg-black/25 py-3 px-2">
            <p className="font-heading text-2xl text-white">{counts.decisions_remaining}</p>
            <p className="text-[10px] font-bold uppercase tracking-wide text-white/70">Decisions Remaining</p>
          </div>
        </div>
        {counts.exempt > 0 && (
          <p className="text-white/60 text-[11px] mt-2">
            Includes {counts.exempt} roster-exempt player{counts.exempt > 1 ? 's' : ''} (e.g., International Player Pathway).
          </p>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4">
          {settings.last_updated && (
            <p className="text-white/60 text-xs">
              Last Updated:{' '}
              {new Date(settings.last_updated).toLocaleString('en-US', {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
              })}
            </p>
          )}
          <div className="flex gap-2">
            <button
              data-testid="roster-view-tracker"
              onClick={() => setView('tracker')}
              className={`px-3 py-1 text-xs font-heading font-medium uppercase tracking-wide border transition-colors ${
                view === 'tracker' ? 'bg-white text-[#5A1414] border-white' : 'text-white/80 border-white/40 hover:border-white'
              }`}
            >
              Tracker
            </button>
            <button
              data-testid="roster-view-projected"
              onClick={() => setView('projected')}
              className={`px-3 py-1 text-xs font-heading font-medium uppercase tracking-wide border transition-colors ${
                view === 'projected' ? 'bg-white text-[#5A1414] border-white' : 'text-white/80 border-white/40 hover:border-white'
              }`}
            >
              Projected 53 ({counts.projected}/53)
            </button>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <p className="px-5 sm:px-8 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] text-slate-500">
        Roster projections and player classifications represent DC Sports Report analysis. Official Washington Commanders transactions and roster designations are identified separately.
      </p>

      {!isFinal && counts.projected !== 53 && (
        <p data-testid="projected-53-review-banner" className="px-5 sm:px-8 py-2.5 bg-amber-50 border-b border-amber-200 text-[11px] font-bold text-amber-800 uppercase tracking-wide">
          Projected 53 needs editor review — currently {counts.projected}/53{counts.projected < 53 ? ` (${53 - counts.projected} spot${53 - counts.projected === 1 ? '' : 's'} open)` : ' (over the limit)'}
        </p>
      )}

      <div className="px-5 sm:px-8 py-6">
        {view === 'projected' ? (
          <div data-testid="projected-53-view">
            <SectionTitle>DC Sports Report Projected 53 — {counts.projected} / 53</SectionTitle>
            <p className="text-xs text-slate-500 mb-4">
              Players classified Roster Lock or Likely In, plus editorial selections. Not the official Washington Commanders roster.
            </p>
            {projected.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Projections coming soon — check back as camp develops.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-x-8">
                {positions.map((pos) => {
                  const items = projected.filter((p) => p.position === pos);
                  if (items.length === 0) return null;
                  return (
                    <div key={pos} className="mb-4">
                      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400 border-b border-slate-100 pb-1 mb-1">
                        {pos} ({items.length})
                      </p>
                      {items.map((p) => (
                        <p key={p.id} className="text-sm text-slate-800 py-0.5">
                          <span className="font-bold">{p.name}</span>
                          {p.jersey && <span className="text-slate-400 text-xs"> #{p.jersey}</span>}
                        </p>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* On the Bubble */}
            {bubble.length > 0 && (
              <div data-testid="bubble-section" className="mb-8">
                <SectionTitle color="#FFB612">On the Bubble</SectionTitle>
                <div className="grid sm:grid-cols-2 gap-x-6 divide-y sm:divide-y-0 divide-slate-100">
                  {bubble.map((p) => (
                    <PlayerRow key={p.id} player={p} />
                  ))}
                </div>
              </div>
            )}

            {/* Stock Watch */}
            {stockWatch.length > 0 && (
              <div data-testid="stock-watch-section" className="mb-8">
                <SectionTitle>Roster Stock Watch</SectionTitle>
                <div className="grid sm:grid-cols-2 gap-3">
                  {stockWatch.map((p) => (
                    <div key={p.id} className={`border p-3 ${p.stock === 'UP' ? 'border-green-700/40 bg-green-50/50' : 'border-red-700/40 bg-red-50/50'}`}>
                      <p className={`text-xs font-bold uppercase tracking-wide flex items-center gap-1 ${p.stock === 'UP' ? 'text-green-800' : 'text-red-800'}`}>
                        {p.stock === 'UP' ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                        Stock {p.stock === 'UP' ? 'Up' : 'Down'}
                      </p>
                      <p className="text-sm font-bold text-slate-900 mt-1">
                        {p.name} <span className="font-medium text-slate-500">— {p.pos_detail || p.position}</span>
                      </p>
                      {p.stock_note && <p className="text-xs text-slate-600 italic mt-1">"{p.stock_note}"</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* What Changed */}
            {changes.length > 0 && (
              <div data-testid="what-changed-section" className="mb-8">
                <SectionTitle color="#002868">What Changed?</SectionTitle>
                <div className="divide-y divide-slate-100">
                  {changes.map((c) => (
                    <p key={c.id} className="text-xs text-slate-700 py-1.5">
                      {c.text}
                      <span className="text-slate-400 ml-2">
                        {new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Cut Tracker */}
            {cutMoves.length > 0 && (
              <div data-testid="cut-tracker-section" className="mb-8">
                <SectionTitle color="#9a3412">Commanders Cut Tracker</SectionTitle>
                <div className="divide-y divide-slate-100">
                  {cutMoves.map((tx) => (
                    <div key={tx.id} className="flex items-start gap-3 py-2">
                      <span className="shrink-0 bg-[#9a3412] text-white text-[10px] font-bold uppercase px-1.5 py-0.5 mt-0.5">{tx.type.replace(/_/g, ' ')}</span>
                      <p className="text-sm text-slate-900"><b>{tx.player_name}</b>{tx.position && <span className="text-slate-500"> — {tx.position}</span>}<span className="text-slate-400 text-xs ml-2">{new Date(tx.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>{tx.note && <span className="block text-xs text-slate-600">{tx.note}</span>}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Roster Additions */}
            {addMoves.length > 0 && (
              <div data-testid="roster-additions-section" className="mb-8">
                <SectionTitle color="#14532d">Latest Roster Additions</SectionTitle>
                <div className="divide-y divide-slate-100">
                  {addMoves.map((tx) => (
                    <div key={tx.id} className="flex items-start gap-3 py-2">
                      <span className="shrink-0 bg-[#14532d] text-white text-[10px] font-bold uppercase px-1.5 py-0.5 mt-0.5">{tx.type.replace(/_/g, ' ')}</span>
                      <p className="text-sm text-slate-900"><b>{tx.player_name}</b>{tx.position && <span className="text-slate-500"> — {tx.position}</span>}<span className="text-slate-400 text-xs ml-2">{new Date(tx.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span></p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Roster Move Timeline */}
            {transactions.length > 0 && (
              <div data-testid="roster-moves-section" className="mb-8">
                <SectionTitle color="#002868">Roster Move Timeline</SectionTitle>
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Official Transactions</p>
                <div className="divide-y divide-slate-100">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-start gap-3 py-2">
                      <span className="shrink-0 bg-[#002868] text-white text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 mt-0.5">
                        {TX_LABELS[tx.type] || tx.type}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-slate-900">
                          <span className="font-bold">{tx.player_name}</span>
                          {tx.position && <span className="text-slate-500"> — {tx.position}</span>}
                          <span className="text-slate-400 text-xs ml-2">
                            {new Date(tx.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </p>
                        {tx.note && <p className="text-xs text-slate-600 mt-0.5">{tx.note}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Position Battles */}
            {battles.length > 0 && (
              <div data-testid="position-battles-section" className="mb-8">
                <SectionTitle>Position Battles to Watch</SectionTitle>
                <div className="grid sm:grid-cols-2 gap-3">
                  {battles.map((b) => (
                    <div key={b.id} className="border border-slate-200 p-3">
                      <p className="font-heading text-sm text-slate-900 uppercase tracking-wide flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-[#5A1414]" />
                        {b.title}
                      </p>
                      {b.players?.length > 0 && (
                        <p className="text-xs font-semibold text-slate-700 mt-1.5">{b.players.join(' · ')}</p>
                      )}
                      {b.description && <p className="text-xs text-slate-600 mt-1">{b.description}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filters */}
            <SectionTitle color="#5A1414">{isFinal ? 'Roster Breakdown' : 'Roster Battle View'}</SectionTitle>
            <div className="flex flex-wrap gap-2 mb-3">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  data-testid={`roster-filter-${f.key.toLowerCase()}`}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1 text-[11px] font-heading font-medium uppercase tracking-wide border transition-colors ${
                    filter === f.key ? 'bg-[#5A1414] text-white border-[#5A1414]' : 'bg-white text-slate-700 border-slate-300 hover:border-[#5A1414]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <input
                data-testid="roster-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search player…"
                className="ml-auto text-[11px] border border-slate-300 px-2 py-1 w-36"
              />
              <select
                data-testid="roster-position-filter"
                value={posFilter}
                onChange={(e) => setPosFilter(e.target.value)}
                className="text-[11px] font-heading uppercase tracking-wide border border-slate-300 px-2 py-1 bg-white"
              >
                <option value="ALL">All Positions</option>
                {positions.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Position groups */}
            {byPosition.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No players match this filter.</p>
            ) : (
              byPosition.map(({ pos, items }) => (
                <div key={pos} data-testid={`position-group-${pos.replace('/', '-')}`} className="mb-5">
                  <div className="flex items-center justify-between bg-slate-100 px-3 py-1.5">
                    <p className="font-heading text-sm text-slate-900 uppercase tracking-wide">{pos}</p>
                    <p className="text-[11px] text-slate-500 uppercase tracking-wide">
                      {activeByPos(pos)} on roster · {settings.position_spots?.[pos] ?? '—'} projected spots
                    </p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {items.map((p) => (
                      <PlayerRow key={p.id} player={p} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default RoadTo53;
